import {mapObject, noop} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {pageLoadID} from 'ClientHeaders';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as context from 'Context';
import AppDispatcher from 'lib/Dispatcher';
import {DEFAULT_DURATION} from 'nav/toasts/ToastConstants';
import DocumentActions from 'notebook/documents/DocumentActions';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import {RowSubmissionRange} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {invalidateDataModSummaryCacheForTable} from 'notebook/structured-tables/DataModificationSummaryQuery';
import {TableValidationError} from 'notebook/structured-tables/StructuredTableApiConstants';
import {
  StructuredTableKind,
  StructuredTableRow,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  buildChangeSummary,
  getAddedRowCount,
  getStructuredTableCellDiffCount,
  getSubmittedModifications,
  isStructuredTableUnsupported,
  StructuredTableDiff,
  structuredTablesContainFinalizedRow,
  TableSubmissionDiffs,
} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {callWithTableValidationBypassModal} from 'notebook/tables/core-ui/modals/TableValidationBypassModal';
import {getEvaluatedRowErrors, hasOnlyOverridableErrors} from 'notebook/tables/errors/ErrorUtil';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {Table} from 'notebook/tables/TableConstants';
import notifications from 'notifications';
import * as BenchlingProfiler from 'performance/BenchlingProfiler';
import * as Perf from 'performance/Perf';
import {toDatadogTagFormat} from 'performance/PerformanceUtils';
import {TABLE_SUBMIT_FINISHED_EVENT, TABLE_SUBMIT_STARTED_EVENT} from 'performance/processNotebookEvents';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {InventoryRegistryDataModAction} from 'samples/getInventoryRegistryChangeSummary';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';

export const WAIT_FOR_ENTRY_SAVED_SECONDS = 30;
export const WAIT_FOR_ENTRY_SAVED_INTERVAL_MS = 500;

export enum ActionResultType {
  SUCCESS = 'SUCCESS',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  ABORTED = 'ABORTED',
}

/**
 * All the possible return values of doPerformAction.
 */
export type ServerSideWriteActionResult<ResponseData> =
  // The server-side action was successful.
  // We should follow up based on the server's responseData.
  | {resultType: ActionResultType.SUCCESS; responseData: ResponseData}
  // The server-side action was partially successful.
  // This occurs when batched table submission fails on a batch other than the first.
  // It is up to the caller to interpret the value of responseData.
  | {
      resultType: ActionResultType.PARTIAL_SUCCESS;
      responseData: ResponseData;
      submittedRange: RowSubmissionRange;
    }
  // The server-side action errored, and the error
  // has been handled inside doPerformAction.
  | {resultType: ActionResultType.ABORTED}
  // The server-side action errored, and the error
  // has not been handled yet. In this case,
  // doPerformAction should not return.
  // Instead, doPerformAction should throw an exception.
  | never;

export function abortAction<ResponseData>(): ServerSideWriteActionResult<ResponseData> {
  LoadingScreen.hide();
  // Reset loading screen message since the action errored
  LoadingScreen.show();
  return {
    resultType: ActionResultType.ABORTED,
  };
}

async function performWithLockAcquisition<ResponseData>({
  entryOrTemplateId,
  doPerformAction,
  dataModsHeader,
}: {
  entryOrTemplateId: string;
  doPerformAction: (
    updateStatus?: (msg: string) => void,
    dataModsHeader?: DataModificationsHeader
  ) => Promise<ServerSideWriteActionResult<ResponseData>>;
  dataModsHeader?: DataModificationsHeader;
}): Promise<ServerSideWriteActionResult<ResponseData>> {
  const lockAcquisitionInterval = setInterval(
    (entryOrTemplateId) => {
      // Force acquire the lock to ensure that our DAO requests
      // are appropriately being queued alongside any other lock acquisition requests for this document
      DocumentEditorLockUtil.forceAcquireLocks(entryOrTemplateId);
    },
    Math.max(BenchlingConfig.ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS / 2, 1) * 1000,
    entryOrTemplateId,
    pageLoadID
  );
  let actionResult: ServerSideWriteActionResult<ResponseData>;
  try {
    actionResult = await doPerformAction(LoadingScreen.set, dataModsHeader);
  } finally {
    clearInterval(lockAcquisitionInterval);
  }
  return actionResult;
}

export async function performServerSideTableWriteActionWithAudits<ResponseData>({
  entryId,
  loadingScreenMessage,
  autoSaverAction,
  doValidationAction,
  doPreSaveAction,
  dataModsItemFn,
  doPerformAction,
  doPostRefresh,
  analyticsActionForPerfTracking,
  structuredTables,
  structuredTableAdapters,
  computedTables,
  tables,
  selection,
}: {
  entryId: string;
  loadingScreenMessage: string;
  autoSaverAction: string;
  doValidationAction?: () => Promise<void>;
  doPreSaveAction?: () => Promise<void>;
  /* Return mapping from item type to api ids of that type that are being modified. */
  dataModsItemFn?: (
    structuredTables: Array<UnknownStructuredTable>,
    structuredTableAdapters: Array<UnknownStructuredTableAdapter>,
    computedTables: Array<EvaluatedTable>,
    tableSubmissionUpdates: Array<StructuredTableDiff>
  ) => {[k: string]: Array<string>};
  doPerformAction: (
    updateStatus?: (msg: string) => void,
    dataModsHeader?: DataModificationsHeader
  ) => Promise<ServerSideWriteActionResult<ResponseData>>;
  doPostRefresh?: (
    actionResult: Exclude<ServerSideWriteActionResult<ResponseData>, {resultType: ActionResultType.ABORTED}>
  ) => Promise<void>;
  analyticsActionForPerfTracking?: string;
  structuredTables: Array<UnknownStructuredTable>;
  structuredTableAdapters: Array<UnknownStructuredTableAdapter>;
  computedTables: Array<EvaluatedTable>;
  tables: Array<Table>;
  selection?: RowSubmissionRange;
}): Promise<void> {
  LoadingScreen.show(loadingScreenMessage);
  let tableSubmissionDiffs: TableSubmissionDiffs;
  let addedRowCount = 0;
  let itemChangeCounts: {[k: string]: number} = {};
  const additionalModifiedItemIds: Array<string> = [];
  const tableApiIds: Array<string> = structuredTables
    .map((table) => table.apiId ?? '')
    .filter((apiId) => apiId !== '');

  try {
    tableSubmissionDiffs = getSubmittedModifications(
      structuredTables,
      structuredTableAdapters,
      computedTables,
      selection
    );
    addedRowCount = getAddedRowCount(structuredTables, selection);

    // data mods for notebook cells when resubmitting a table
    if (
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_NOTEBOOK &&
      !isStructuredTableUnsupported(structuredTables) &&
      structuredTablesContainFinalizedRow(structuredTables)
    ) {
      itemChangeCounts.cell = getStructuredTableCellDiffCount(tableSubmissionDiffs.tableSubmissionUpdates);
    }

    // add any additional data mod items
    if (dataModsItemFn != null) {
      const extraItemIds = dataModsItemFn(
        structuredTables,
        structuredTableAdapters,
        computedTables,
        tableSubmissionDiffs.tableSubmissionUpdates
      );
      for (const itemIds of Object.values(extraItemIds)) {
        additionalModifiedItemIds.push(...new Set(itemIds));
      }
      itemChangeCounts = {
        ...itemChangeCounts,
        ...mapObject(extraItemIds, (itemIds: Array<string>) => itemIds.length),
      };
    }
  } finally {
    LoadingScreen.hide();
  }

  const writeActionWithoutDataMods = (
    bypassValidationReason?: DataModificationsHeader,
    bypassValidation?: boolean
  ): Promise<void> =>
    performServerSideWriteAction<ResponseData>({
      entryId,
      loadingScreenMessage,
      autoSaverAction,
      doValidationAction,
      doPreSaveAction,
      doPerformAction,
      doPostRefresh,
      analyticsActionForPerfTracking,
      dataModsHeader: bypassValidationReason,
      bypassValidation,
    });

  const writeActionWithDataMods = (
    bypassValidationReason?: DataModificationsHeader,
    bypassValidation?: boolean
  ): Promise<void> => {
    const changeSummary = buildChangeSummary(itemChangeCounts, addedRowCount);
    return performServerSideWriteActionWithAudits({
      entryId,
      changeSummary,
      loadingScreenMessage,
      autoSaverAction,
      doValidationAction,
      doPreSaveAction,
      doPerformAction,
      doPostRefresh,
      analyticsActionForPerfTracking,
      areFeatureDataModsEnabled: true,
      additionalApiIdsForDataMods: additionalModifiedItemIds,
      additionalReasonCodeAndComments: bypassValidationReason,
      bypassValidation,
      tableApiIds,
    });
  };

  const callWithoutCheckingDataMods = !Object.keys(itemChangeCounts).length;
  const tableErrors = tables.map((table) => getEvaluatedRowErrors(table, selection)).flat(1) ?? [];
  const useValidationErrorBypass =
    BenchlingConfig.STRUCTURED_TABLE_ENABLE_VALIDATION_BYPASS && hasOnlyOverridableErrors(tableErrors);

  if (!useValidationErrorBypass) {
    if (callWithoutCheckingDataMods) {
      // 1: Initial table submit -> don't need to check data mods settings
      await writeActionWithoutDataMods();
    } else {
      // 2: Table updated -> need to check data mods settings
      await writeActionWithDataMods();
    }
  } else if (callWithoutCheckingDataMods) {
    // 3: Initial table submit, has only bypassable errors and feature flag is enabled -> open validation bypass modal
    await callWithTableValidationBypassModal({
      onRequestSubmit: (bypassValidationReason?: DataModificationsHeader) =>
        writeActionWithoutDataMods(bypassValidationReason, true),
      errorRows: tableErrors,
    });
  } else {
    // 4: Table updated -> open validation bypass modal and then check data mods settings
    const bypassValidationReason = await callWithTableValidationBypassModal({
      onRequestSubmit: () => Promise.resolve(), // noop, don't call write logic yet, will call below in data mods modal function
      errorRows: tableErrors,
    });
    if (bypassValidationReason) {
      await writeActionWithDataMods(bypassValidationReason, true);
    }
  }
}

export async function performServerSideWriteActionWithAudits<ResponseData>({
  entryId,
  changeSummary,
  loadingScreenMessage,
  autoSaverAction,
  doValidationAction,
  doPreSaveAction,
  doPerformAction,
  doPostRefresh,
  analyticsActionForPerfTracking,
  areFeatureDataModsEnabled,
  onRequestClose = noop,
  additionalApiIdsForDataMods,
  additionalReasonCodeAndComments,
  bypassValidation,
  tableApiIds = [],
}: {
  entryId: string;
  changeSummary: string;
  loadingScreenMessage: string;
  autoSaverAction: string;
  doValidationAction?: () => Promise<void>;
  doPreSaveAction?: () => Promise<void>;
  doPerformAction: (
    updateStatus?: (msg: string) => void,
    dataModsHeader?: DataModificationsHeader
  ) => Promise<ServerSideWriteActionResult<ResponseData>>;
  doPostRefresh?: (
    actionResult: Exclude<ServerSideWriteActionResult<ResponseData>, {resultType: ActionResultType.ABORTED}>
  ) => Promise<void>;
  analyticsActionForPerfTracking?: string;
  areFeatureDataModsEnabled: boolean;
  /* If provided, send alongside entryId for determining whether data mods are needed on the items when
   * areFeatureDataModsEnabled is true. */
  additionalApiIdsForDataMods?: Array<string>;
  onRequestClose?: () => void;
  additionalReasonCodeAndComments?: DataModificationsHeader;
  bypassValidation?: boolean;
  tableApiIds?: Array<string>;
}): Promise<void> {
  const performAction = async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
    await performServerSideWriteAction<ResponseData>({
      entryId,
      loadingScreenMessage,
      autoSaverAction,
      doValidationAction,
      doPreSaveAction,
      doPerformAction,
      doPostRefresh,
      analyticsActionForPerfTracking,
      dataModsHeader,
      bypassValidation,
    });
    if (
      areFeatureDataModsEnabled &&
      tableApiIds &&
      BenchlingConfig.ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY
    ) {
      tableApiIds.forEach((apiId) => {
        invalidateDataModSummaryCacheForTable(apiId);
      });
    }
  };
  if (areFeatureDataModsEnabled) {
    await callAsyncWithDataModifications({
      changeSummary,
      onPerformAction: performAction,
      onModalCancel: onRequestClose,
      analyticsParams: {
        module: 'ELN',
        actionOrigin: ActionOrigin.NOTEBOOK,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: 1,
        entryId,
      },
      apiIds: [entryId, ...(additionalApiIdsForDataMods ?? [])],
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_NOTEBOOK,
      additionalReasonCodeAndComments,
    });
  } else {
    await performAction();
  }
}

interface SLIInstrumentationOptions {
  entryId: string;
  actionName: string;
  loadingScreenMessage: string;
  documentType: ModelTypeConstants;
  withValidation: boolean;
}

/**
 * This instrumentation uses the Perf API.
 * @deprecated use startNewInstrumentation instead
 */
function startOldInstrumentation({
  entryId,
  actionName,
  loadingScreenMessage,
  documentType,
  withValidation,
}: SLIInstrumentationOptions): () => void {
  const id = Perf.start(
    'entry_server_submission',
    {
      enabled: true,
      getMetadata: () => ({
        entryId,
        autoSaverAction: actionName,
        loadingScreenMessage,
      }),
    },
    undefined,
    {
      actionName,
      documentType,
      withValidation,
    }
  );
  Perf.markStart(id);
  return () => {
    Perf.markEnd(id);
  };
}

/**
 * This instrumentation uses the BenchlingProfiler API.
 */
function startNewInstrumentation({
  entryId,
  actionName,
  loadingScreenMessage,
  documentType,
  withValidation,
}: SLIInstrumentationOptions): () => void {
  const id = uuidv4();
  BenchlingProfiler.logProfilerTimingEvent({...TABLE_SUBMIT_STARTED_EVENT, id});
  return () => {
    BenchlingProfiler.logProfilerTimingEvent(
      {...TABLE_SUBMIT_FINISHED_EVENT, id},
      {
        actionMetadata: {
          entry_id: toDatadogTagFormat(entryId),
          action_name: toDatadogTagFormat(actionName),
          loading_screen_message: toDatadogTagFormat(loadingScreenMessage, {removeInvalidCharacters: true}),
        },
        distributionMetadata: {
          action_name: toDatadogTagFormat(actionName),
          document_type: toDatadogTagFormat(documentType),
          with_validation: withValidation,
        },
      }
    );
  };
}

/**
 * Begins instrumentation of the performServerSideWriteAction function with SLI events.
 *
 * Note that this instrumentation is currently undergoing a migration from an old API that is instrumented in
 * milliseconds to a new API that is instrumented in seconds. This function will start instrumentation for
 * both methods, and will return a function that finishes instrumentation for both methods. After we collect
 * some new data, we will remove the old instrumentation and this wrapper function can be removed.
 *
 * @returns a function that finishes the instrumentation.
 */
function startInstrumentation(options: SLIInstrumentationOptions): () => void {
  const finishOldInstrumentation = startOldInstrumentation(options);
  const finishNewInstrumentation = startNewInstrumentation(options);
  return () => {
    finishNewInstrumentation();
    finishOldInstrumentation();
  };
}

async function refreshDocument(documentId: string): Promise<boolean> {
  const documentType = APIUtil.apiIdToType(documentId);
  assert(APIUtil.isExecutionDocument(documentId), 'Unexpected entry type', {documentId});

  let success = false;
  let refreshAttempts = 0; // The document refetch may be retried once if it fails.
  while (!success && refreshAttempts < 2) {
    refreshAttempts++;
    try {
      if (documentType === ModelTypeConstants.STAGE_ENTRY) {
        await StageEntryActions.fetchEntryDetails(documentId);
        success = true;
      } else if (documentType === ModelTypeConstants.DOCUMENT) {
        success = await DocumentActions.open(documentId, {bumpVersion: true, documentOp: 'eval-document'});
      } else {
        success = await EntryActions.open(documentId, {bumpVersion: true, documentOp: 'eval-document'});
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      // We swallow this error to simplify branch handling needed by callers. We return false
      // in all cases here and handle failure in that way, ignoring that the various doc
      // fetching methods above may throw in some failure cases and return false in others.
    }
  }
  if (!success) {
    logging.error('Failed to reload document after server-side write action.', {
      extra: {documentId, documentType},
    });
  }
  return success;
}

/**
 * Trigger a backend ActionFunction that will mutate a note linked object in the entry.
 *
 * @param entryId - the entry containing the note linked object we are acting on
 * @param loadingScreenMessage - what we should show on the loading screen
 * @param autoSaverAction - indicate to the backend what the action was that
 *                          caused the entry to save
 * @param doValidationAction - a callback that performs any validation checks. Can be bypassed with the bypassValidation param
 * @param doPreSaveAction - a callback that allows the caller to perform some synchronous action before
 *                          the Entry or StageEntry is saved.  This can be used to do a backfill or some
 *                          other operation that the doPerformAction relies upon.
 * @param doPerformAction - a callback that performs the call to the backend
 *                          ActionFunction
 * @param doPostRefresh - actions which we want to perform once the
 *                        doPerformAction is complete.  This method is awaited
 *                        and once it returns, the loading screen will be hidden.
 * @param analyticsActionForPerfTracking - if provided, the analytics event we should capture `perf` for
 * @param dataModsHeader header with data modifications information to be sent from the Data Mods modal
 *                       through the action to the HTTP request. Should only be set if using performServerSideWriteActionWithAudits
 * @param bypassValidation - if true, doValidationAction will be skipped
 */
export async function performServerSideWriteAction<ResponseData>({
  entryId,
  loadingScreenMessage,
  autoSaverAction,
  doValidationAction,
  doPreSaveAction,
  doPerformAction,
  doPostRefresh,
  analyticsActionForPerfTracking,
  dataModsHeader,
  bypassValidation,
}: {
  entryId: string;
  loadingScreenMessage: string;
  autoSaverAction: string;
  doValidationAction?: () => Promise<void>;
  doPreSaveAction?: () => Promise<void>;
  doPerformAction: (
    updateStatus?: (msg: string) => void,
    dataModsHeader?: DataModificationsHeader
  ) => Promise<ServerSideWriteActionResult<ResponseData>>;
  doPostRefresh?: (
    actionResult: Exclude<ServerSideWriteActionResult<ResponseData>, {resultType: ActionResultType.ABORTED}>
  ) => Promise<void>;
  analyticsActionForPerfTracking?: string;
  dataModsHeader?: DataModificationsHeader;
  bypassValidation?: boolean;
}): Promise<void> {
  const documentType = APIUtil.apiIdToType(entryId);

  const finishInstrumentation = startInstrumentation({
    entryId,
    actionName: autoSaverAction,
    loadingScreenMessage,
    documentType,
    withValidation: doValidationAction != null && !bypassValidation,
  });

  const startTime = Date.now();

  // If we invalidate the entry we need to refresh it
  // Otherwise, the user will get stuck on a previous entry version
  let wasEntryInvalidated = false;
  let wasRefreshAttempted = false;

  LoadingScreen.show(loadingScreenMessage);
  try {
    if (doPreSaveAction) {
      await doPreSaveAction();
    }

    // TODO(BNCH-119991): Add queueSave support for Document and StageEntry
    if (BenchlingConfig.ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES && documentType === ModelTypeConstants.ENTRY) {
      await context.usingAsync({immediateAutoSaverQueueSaveAction: autoSaverAction}, null, async () => {
        await EntryActions.queueSave(entryId);
      });
    } else {
      context.using({immediateAutoSaverQueueSaveAction: autoSaverAction}, null, () => {
        assert(APIUtil.isExecutionDocument(entryId), 'Unexpected entry type', {entryId});
        if (documentType === ModelTypeConstants.STAGE_ENTRY) {
          StageEntryActions.save(entryId);
        } else if (documentType === ModelTypeConstants.DOCUMENT) {
          DocumentActions.save(entryId);
        } else {
          EntryActions.save(entryId);
        }
      });
    }

    const saved = await waitForEntryOrTemplateSaved(
      entryId,
      WAIT_FOR_ENTRY_SAVED_INTERVAL_MS,
      WAIT_FOR_ENTRY_SAVED_SECONDS
    );
    if (!saved) {
      logging.info(
        `Saving entry took more than ${WAIT_FOR_ENTRY_SAVED_SECONDS} seconds. The user was shown a loading screen with "${loadingScreenMessage}".`,
        {extra: {entryId}}
      );
      notifications.error(
        `Action requires the entry to be saved.
        Please wait until the entry is saved before trying again.`
      );
    } else {
      assert(APIUtil.isExecutionDocument(entryId), 'Unexpected entry type', {entryId});
      if (doValidationAction && !bypassValidation) {
        await doValidationAction();
      }
      if (documentType === ModelTypeConstants.STAGE_ENTRY) {
        StageEntryActions.invalidate(entryId);
      } else if (documentType === ModelTypeConstants.DOCUMENT) {
        DocumentActions.invalidate(entryId);
      } else {
        EntryActions.invalidate(entryId);
      }
      wasEntryInvalidated = true;

      const actionResult =
        documentType !== ModelTypeConstants.STAGE_ENTRY
          ? await performWithLockAcquisition({
              entryOrTemplateId: entryId,
              doPerformAction,
              dataModsHeader,
            })
          : await doPerformAction(LoadingScreen.set, dataModsHeader);
      if (actionResult.resultType === ActionResultType.ABORTED) {
        assert(APIUtil.isExecutionDocument(entryId), 'Unexpected entry type', {entryId});
        if (documentType === ModelTypeConstants.STAGE_ENTRY) {
          StageEntryActions.revalidate(entryId);
        } else if (documentType === ModelTypeConstants.DOCUMENT) {
          DocumentActions.revalidate(entryId);
        } else {
          EntryActions.revalidate(entryId);
        }
        wasEntryInvalidated = false;
        return;
      } else if (actionResult.resultType === ActionResultType.PARTIAL_SUCCESS) {
        notifications.warn('Action partially succeeded. Please check for errors and try again.');
      }

      wasRefreshAttempted = true;
      const documentRefreshed = await refreshDocument(entryId);
      if (!documentRefreshed) {
        notifications.error(
          'Action succeeded, but the document could not be refreshed. Please reload the page to continue.',
          {closeable: false}
        );
        return;
      }
      if (doPostRefresh != null) {
        // Wait for the post refresh callback to be complete before hiding the
        // loading screen
        await doPostRefresh(actionResult);
      }
      if (documentType === ModelTypeConstants.STAGE_ENTRY) {
        StageEntryActions.postServerSideUpdate(entryId);
      } else if (documentType === ModelTypeConstants.DOCUMENT) {
        DocumentActions.postServerSideUpdate(entryId);
      } else {
        EntryActions.postServerSideUpdate(entryId);
      }
    }
  } catch (e) {
    if (e instanceof TableValidationError) {
      if (wasEntryInvalidated) {
        wasRefreshAttempted = true;
        const documentRefreshed = await refreshDocument(entryId);
        if (!documentRefreshed) {
          notifications.error(
            'There were validation errors, and the document could not be refreshed. Please reload the page to continue.',
            {closeable: false}
          );
          return;
        }
      }
      handleValidationError(e, entryId);
    } else {
      throw e;
    }
  } finally {
    // Attempt to refresh the document in cases where it was invalidated but a refresh has not yet been attempted.
    // Unexpected server errors hit this case.
    if (wasEntryInvalidated && !wasRefreshAttempted) {
      const documentRefreshed = await refreshDocument(entryId);
      if (!documentRefreshed) {
        notifications.error('The document could not be refreshed. Please reload the page to continue.', {
          closeable: false,
        });
      }
    }
    LoadingScreen.hide();
    const endTime = Date.now();
    if (analyticsActionForPerfTracking != null) {
      analytics.track('perf', analyticsActionForPerfTracking, {milliseconds: endTime - startTime});
    }

    finishInstrumentation();
  }
}

export async function performClientSideWriteAction<ResponseData>({
  entryOrTemplateId,
  loadingScreenMessage,
  autoSaverAction,
  doPerformAction,
  analyticsEvent = null,
  analyticsMetadata = {},
}: {
  entryOrTemplateId: string;
  loadingScreenMessage: string;
  autoSaverAction: string;
  doPerformAction: () => Promise<ResponseData>;
  analyticsEvent: analytics.AnalyticsEvent | null;
  analyticsMetadata?: object;
}): Promise<void> {
  const startTime = Date.now();
  const entryOrTemplateType = APIUtil.apiIdToType(entryOrTemplateId);

  LoadingScreen.show(loadingScreenMessage);
  try {
    let saved;
    if (entryOrTemplateType === ModelTypeConstants.STAGE_CONFIGURATION) {
      // There is no auto-save
      saved = true;
    } else {
      // TODO(BNCH-119991): Add queueSave support for document types other than Entry
      if (
        BenchlingConfig.ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES &&
        entryOrTemplateType === ModelTypeConstants.ENTRY
      ) {
        await context.usingAsync({immediateAutoSaverQueueSaveAction: autoSaverAction}, null, async () => {
          await EntryActions.queueSave(entryOrTemplateId);
        });
      } else {
        context.using({immediateAutoSaverQueueSaveAction: autoSaverAction}, null, async () => {
          if (entryOrTemplateType === ModelTypeConstants.STAGE_ENTRY) {
            StageEntryActions.save(entryOrTemplateId);
          } else if (entryOrTemplateType === ModelTypeConstants.ENTRY) {
            await EntryActions.queueSave(entryOrTemplateId);
          } else if (entryOrTemplateType === ModelTypeConstants.TEMPLATE) {
            TemplateActions.save(entryOrTemplateId);
          } else if (entryOrTemplateType === ModelTypeConstants.SUBTEMPLATE) {
            SubtemplateActions.save(entryOrTemplateId);
          } else if (entryOrTemplateType === ModelTypeConstants.DOCUMENT) {
            DocumentActions.save(entryOrTemplateId);
          } else {
            assert(
              (entryOrTemplateType as string) === (ModelTypeConstants.ENTRY as string),
              `Unexpected entry type "${entryOrTemplateType}"`,
              {
                entryOrTemplateId,
              }
            );
          }
        });
      }

      saved = await waitForEntryOrTemplateSaved(
        entryOrTemplateId,
        WAIT_FOR_ENTRY_SAVED_INTERVAL_MS,
        WAIT_FOR_ENTRY_SAVED_SECONDS
      );
    }

    if (!saved) {
      logging.info(
        `Saving entry took more than ${WAIT_FOR_ENTRY_SAVED_SECONDS} seconds. The user was shown a loading screen with "${loadingScreenMessage}".`,
        {
          extra: {entryOrTemplateId},
        }
      );
      notifications.error(
        `Action requires the entry to be saved.
        Please wait until the entry is saved before trying again.`
      );
    } else {
      await doPerformAction();
    }
  } finally {
    LoadingScreen.hide();
    const endTime = Date.now();
    if (analyticsEvent != null) {
      analytics.deprecatedTrackEvent(analyticsEvent, {
        milliseconds: endTime - startTime,
        ...analyticsMetadata,
      });
    }
  }
}

/**
 * Returns the configured batched submission batch size for the specified table kind, or null if batching
 * is not configured for that kind.
 */
export function getSubmissionBatchSize(tableKind: StructuredTableKind): number | null {
  if (BenchlingConfig.ELN_TABLE_SUBMISSION_CUSTOM_BATCH_SIZES[tableKind]) {
    return BenchlingConfig.ELN_TABLE_SUBMISSION_CUSTOM_BATCH_SIZES[tableKind];
  }
  return BenchlingConfig.ELN_TABLE_SUBMISSION_GLOBAL_BATCH_SIZES[tableKind] ?? null;
}

export function isBatchingRequired(
  computedTable: Array<Array<EvaluatedCell>>,
  batchSize: number | null,
  selection?: RowSubmissionRange
): boolean {
  if (!batchSize) {
    return false;
  }
  return selection != null ? selection.end - selection.start > batchSize : computedTable.length > batchSize;
}

export function getAllBatchRanges(
  batchSize: number | null,
  totalNumItems: number,
  selection?: {start: number; end: number}
): Array<RowSubmissionRange> {
  const _batchSize = batchSize ?? totalNumItems;
  const startIndex = selection?.start ?? 0;
  const endIndex = selection?.end ?? totalNumItems;
  const batchRanges = [];
  for (let batchStartIndex = startIndex; batchStartIndex < endIndex; batchStartIndex += _batchSize) {
    const batchEndIndex = Math.min(batchStartIndex + _batchSize, endIndex);
    batchRanges.push({start: batchStartIndex, end: batchEndIndex});
  }
  return batchRanges;
}

/**
 * Generator for submitting a Structured Table as a series of submission batches.
 */
export function* batchedSubmissionGenerator(
  computedTable: Array<Array<EvaluatedCell>>,
  batchSize: number | null,
  structuredTable: UnknownStructuredTable,
  selection?: RowSubmissionRange
): Generator<{
  range: RowSubmissionRange;
  structuredTableRowsBatch: Array<StructuredTableRow<unknown>>;
  evaluatedRowBatch: Array<Array<EvaluatedCell>>;
}> {
  const batchRanges = getAllBatchRanges(batchSize, computedTable.length, selection);
  for (const range of batchRanges) {
    const structuredTableRowsBatch = structuredTable.rows.slice(range.start, range.end);
    const evaluatedRowBatch = computedTable.slice(range.start, range.end);

    yield {
      range,
      structuredTableRowsBatch,
      evaluatedRowBatch,
    };
  }
}

/**
 * Generic method to handle dispatching table validation errors
 */
export function handleValidationError(e: Error, entryOrTemplateId: string): void {
  if (e instanceof TableValidationError) {
    notifications.error(e.userMessage, {duration: DEFAULT_DURATION});
    AppDispatcher.dispatch({
      actionType: EventConstants.STRUCTURED_TABLE_VALIDATION_ERRORS,
      entryId: entryOrTemplateId,
      validationErrorResponse: e.validationResponse,
    });
  } else {
    throw e;
  }
}
